import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  ESTIMATED_FLOOR_LEVELS_GEOCOMM,
  VERTICAL_LOCATION_CONTENT_GEOCOMM,
} from '@rsos/capstone/src/constants/capabilities';
import Box from '@rsos/components/capstone/base/Box';
import { PHASE } from '@rsos/constants/locationTypes';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import getExtraInfoOrMeta from '@rsos/utils/getExtraInfoOrMeta';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import usePortalType from '@rsos/utils/usePortalType';
import LocationTime from '../DataCards/DataCardLocation/LocationTime';
import { getDataSource, isSourceAniAli } from '../Location/helpers';
import { HistoricLocationsToggle } from '../LocationList/HistoricLocationsToggle';
import Altitude from './Altitude';
import EstimatedAddressLabel from './EstimatedAddressLabel';
import EstimatedFloorLevel from './EstimatedFloorLevel';
import LatLong from './LatLong';
import LocationAddress from './LocationAddress';
import SecondaryLocation from './SecondaryLocation';
import StreetView from './StreetView';
import Uncertainty from './Uncertainty';
import What3words from './What3words';
import { GOOGLE } from './constants/dataSources';
import {
  RowBox,
  LocationAddressContainer,
  SecondaryText,
} from './styles/common.styles';

const MostRecent = () => {
  const { isPremiumUser, portalType } = usePortalType();

  const { selectedCallerID, callQueue } = useSelector(
    state => state.emergencyCalls,
  );
  const permissions = useSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, 'capstone'),
  );
  const activeCapabilities = useSelector(
    state => state.psaps.currentPSAP.activeCapabilities,
  );

  const { locations, secondaryLocations } = useSelector(
    state => state.emergencyCalls.callQueue[selectedCallerID]?.core,
  );

  const {
    mostRecentLocation,
    mostRecentSecondaryLocation,
    mostRecentSecondaryLocationIndex,
  } = getMostRecentLocations(callQueue[selectedCallerID]);

  const mostRecentLoc = mostRecentLocation || mostRecentSecondaryLocation;

  const locationsLength = locations?.length;

  const { payloadID, requestID } = getExtraInfoOrMeta(
    locationsLength ? mostRecentLocation : mostRecentLoc,
  );
  const { irpVersion } = useSelector(state => state.sinatra.irp);

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const source = getDataSource(mostRecentLoc);
  const isAniAli = isSourceAniAli(mostRecentLoc);
  const isGoogle = source === GOOGLE;

  const isSecondaryLoc =
    isAniAli &&
    mostRecentLoc?.class_of_service_category &&
    mostRecentLoc?.class_of_service_category?.startsWith(PHASE);

  const {
    altitude,
    altitude_uncertainty_radius,
    latitude,
    longitude,
    location_time,
    rg_floor,
    rg_exact_location,
    uncertainty_confidence,
    uncertainty_radius,
    rgAddressProvider = '',
  } = mostRecentLoc;

  const indoorLocation = rg_exact_location || rg_floor;

  const timeStamp = parseFloat(location_time);

  const hasEstimatedFloorLevelAccess =
    !!activeCapabilities[ESTIMATED_FLOOR_LEVELS_GEOCOMM] &&
    !!permissions?.includes(
      activeCapabilities?.[ESTIMATED_FLOOR_LEVELS_GEOCOMM]?.name?.toUpperCase(),
    );

  const canDisplayEstimatedFloorLevel = useMemo(() => {
    return (
      // TODO: Enable isAniAli in the next phase
      isGoogle && !!hasEstimatedFloorLevelAccess
    );
  }, [isGoogle, hasEstimatedFloorLevelAccess]);

  const shouldDisplayEstimatedFloorLevel =
    canDisplayEstimatedFloorLevel && !!altitude;
  const isVerticalLocContentChecked =
    !!activeCapabilities[VERTICAL_LOCATION_CONTENT_GEOCOMM];

  return (
    <Box data-name="most-recent-location">
      <Box mt={3} mb={isAniAli ? 0 : 3} mx={2}>
        <Box mx={2}>
          <LocationTime
            {...{ timeStamp }}
            size="xsmall"
            timeAgoTextSize="small"
            timeBoxTextSize="xsmall"
          />
        </Box>
        <RowBox noBg align={'flex-start'}>
          <LocationAddressContainer br={'5px'} width={'100%'}>
            <LocationAddress
              {...{ selectedCallerID }}
              isHoverable
              irpVersion={irpVersion}
              payloadID={payloadID}
              requestID={requestID}
            />
          </LocationAddressContainer>
          {locationsLength > 1 && (
            <HistoricLocationsToggle
              irpVersion={irpVersion}
              locations={locations}
            />
          )}
        </RowBox>
        {!isAniAli && (
          <SecondaryText pl={'8px'}>
            <span>Estimated address </span>
            {rgAddressProvider ? <span>from {rgAddressProvider} </span> : ''}
            <span>| VERIFY with caller</span>
          </SecondaryText>
        )}
      </Box>
      <LatLong
        longitude={longitude}
        latitude={latitude}
        irpVersion={irpVersion}
        payloadID={payloadID}
        requestID={requestID}
        isHoverable
      />
      <What3words
        location={mostRecentLoc}
        isHoverable
        irpVersion={irpVersion}
        payloadID={payloadID}
        requestID={requestID}
      />
      <Uncertainty
        uncertaintyRadius={uncertainty_radius}
        confidence={uncertainty_confidence}
        irpVersion={irpVersion}
        payloadID={payloadID}
        requestID={requestID}
        isHoverable
      />
      <Altitude
        altitude={altitude}
        indoorLocation={indoorLocation}
        altitudeUncertaintyRadius={altitude_uncertainty_radius}
        irpVersion={irpVersion}
        payloadID={payloadID}
        requestID={requestID}
        isHoverable
      />

      <StreetView
        longitude={longitude}
        latitude={latitude}
        irpVersion={irpVersion}
        payloadID={payloadID}
        requestID={requestID}
        portalType={portalType}
        isEsriMap={hasEsriMapAccess}
        isHoverable
      />
      {shouldDisplayEstimatedFloorLevel && (
        <EstimatedFloorLevel
          isHoverable
          irpVersion={irpVersion}
          payloadID={payloadID}
          requestID={requestID}
          longitude={longitude}
          latitude={latitude}
          altitude={altitude}
          portalType={portalType}
          uncertaintyRadius={uncertainty_radius}
          altitudeUncertaintyRadius={altitude_uncertainty_radius}
          isVerticalLocContentChecked={isVerticalLocContentChecked}
        />
      )}

      {secondaryLocations &&
        secondaryLocations.length > 0 &&
        !isSecondaryLoc && (
          <SecondaryLocation
            secondaryLocation={mostRecentSecondaryLocation}
            secondaryLocationIndex={mostRecentSecondaryLocationIndex}
          />
        )}
      <EstimatedAddressLabel
        isMostRecent
        addressSingleLine={mostRecentLoc?.address_single_line}
        classOfServiceCategory={mostRecentLoc?.class_of_service_category}
        classOfService={mostRecentLoc?.class_of_service}
      />
    </Box>
  );
};

export default MostRecent;
